import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { toast } from 'sonner';
import { Moon, Sun, Bell, Shield, LogOut, ChevronRight, User, Monitor, CreditCard, CalendarDays, Loader2, PauseCircle, PlayCircle, HeartHandshake } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

import AppLogo from '../components/AppLogo';

const SettingsPage = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [loading, setLoading] = useState(true);
  const [gcalStatus, setGcalStatus] = useState({ connected: false, google_email: null });
  const [gcalLoading, setGcalLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [availability, setAvailability] = useState({ paused: false, paused_until: null, reason: '' });
  const [pauseDate, setPauseDate] = useState('');
  const [pauseReason, setPauseReason] = useState('');
  const [pauseLoading, setPauseLoading] = useState(false);

  const applyTheme = useCallback((newTheme) => {
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', newTheme);
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user-settings`);
      if (res.data.theme) {
        setTheme(res.data.theme);
        applyTheme(res.data.theme);
      }
    } catch (e) { toast.error('Could not load settings'); } finally { setLoading(false); }
  }, [applyTheme]);

  useEffect(() => { fetchSettings(); fetchGcalStatus(); fetchAvailability(); }, [fetchSettings]);

  useEffect(() => {
    const gcalParam = searchParams.get('gcal');
    if (gcalParam === 'connected') {
      toast.success('Google Calendar connected!');
      fetchGcalStatus();
      searchParams.delete('gcal');
      setSearchParams(searchParams, { replace: true });
    } else if (gcalParam === 'error') {
      toast.error('Failed to connect Google Calendar');
      searchParams.delete('gcal');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchGcalStatus = async () => {
    try {
      const res = await axios.get(`${API}/gcal/status`);
      setGcalStatus(res.data);
    } catch (e) { console.warn('Google Calendar status not available'); }
  };

  const handleConnectGcal = async () => {
    setGcalLoading(true);
    try {
      const res = await axios.get(`${API}/gcal/connect`);
      window.location.href = res.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start Google connection');
      setGcalLoading(false);
    }
  };

  const handleDisconnectGcal = async () => {
    if (!window.confirm('Disconnect Google Calendar? Events already synced will remain.')) return;
    setGcalLoading(true);
    try {
      await axios.post(`${API}/gcal/disconnect`);
      toast.success('Google Calendar disconnected');
      setGcalStatus({ connected: false, google_email: null });
    } catch (e) {
      toast.error('Failed to disconnect');
    } finally { setGcalLoading(false); }
  };

  const handleSyncGcal = async () => {
    setSyncLoading(true);
    try {
      const res = await axios.post(`${API}/gcal/sync`);
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally { setSyncLoading(false); }
  };

  const fetchAvailability = async () => {
    try {
      const res = await axios.get(`${API}/availability/status`);
      setAvailability(res.data);
      if (res.data.paused_until) setPauseDate(res.data.paused_until.split('T')[0]);
      if (res.data.reason) setPauseReason(res.data.reason);
    } catch (e) { console.warn('Availability status not set yet'); }
  };

  const handlePause = async () => {
    if (!pauseDate) { toast.error('Pick a date'); return; }
    setPauseLoading(true);
    try {
      await axios.post(`${API}/availability/pause`, {
        paused_until: new Date(pauseDate + 'T23:59:59Z').toISOString(),
        reason: pauseReason,
      });
      toast.success('Notifications paused');
      fetchAvailability();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to pause');
    } finally { setPauseLoading(false); }
  };

  const handleResume = async () => {
    setPauseLoading(true);
    try {
      await axios.post(`${API}/availability/resume`);
      toast.success('Welcome back! Notifications resumed.');
      setAvailability({ paused: false, paused_until: null, reason: '' });
      setPauseDate('');
      setPauseReason('');
    } catch (e) {
      toast.error('Failed to resume');
    } finally { setPauseLoading(false); }
  };

  const toggleTheme = async () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    applyTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    try {
      await axios.put(`${API}/user-settings`, { theme: newTheme });
    } catch (e) { console.warn('Theme sync skipped:', e.message); }
  };

  const handleLogout = () => {
    applyTheme('light');
    localStorage.removeItem('theme');
    logout();
    navigate('/individuals');
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-200 border-t-cyan-600"></div></div>;
  }

  return (
    <div className="min-h-screen pb-24 bg-background" data-testid="settings-page">
      <header className="surface-header sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center gap-3">
          <AppLogo />
          <div>
          <h1 className="text-xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Settings</h1>
          <p className="text-xs text-muted-foreground">Customize your experience</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-4 space-y-4">
        {/* Appearance */}
        <section className="surface-card overflow-hidden" data-testid="appearance-section">
          <div className="px-5 py-3.5 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <Monitor className="w-4 h-4 text-cyan-600" />
              <h3 className="text-sm font-semibold text-foreground">Appearance</h3>
            </div>
          </div>
          <div className="px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {theme === 'dark' ? <Moon className="w-5 h-5 text-indigo-400" /> : <Sun className="w-5 h-5 text-amber-500" />}
                <div>
                  <p className="text-sm font-medium text-foreground">Dark Mode</p>
                  <p className="text-[11px] text-muted-foreground">{theme === 'dark' ? 'Dark theme active' : 'Light theme active'}</p>
                </div>
              </div>
              <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} data-testid="dark-mode-toggle" />
            </div>
          </div>
        </section>

        {/* Notifications Link */}
        <button
          onClick={() => navigate('/notification-settings')}
          className="w-full surface-card px-5 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
          data-testid="notifications-link"
        >
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-cyan-600" />
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">Notification Preferences</p>
              <p className="text-[11px] text-muted-foreground">Email, phone, and in-app delivery settings</p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </button>

        {/* Profile Link */}
        <button
          onClick={() => navigate('/profile')}
          className="w-full surface-card px-5 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
          data-testid="profile-link"
        >
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-cyan-600" />
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">Profile</p>
              <p className="text-[11px] text-muted-foreground">Edit your name, bio, and profile picture</p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </button>

        {/* Care Profile Link */}
        <button
          onClick={() => navigate('/care-profile')}
          className="w-full surface-card px-5 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
          data-testid="care-profile-link"
        >
          <div className="flex items-center gap-3">
            <HeartHandshake className="w-5 h-5 text-cyan-600" />
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">Care Profile™</p>
              <p className="text-[11px] text-muted-foreground">Discover how you give and receive care</p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </button>

        {/* Billing Link */}
        <button
          onClick={() => navigate('/billing')}
          className="w-full surface-card px-5 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
          data-testid="billing-link"
        >
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-cyan-600" />
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">Subscription & Billing</p>
              <p className="text-[11px] text-muted-foreground">Manage your plan or upgrade</p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </button>

        {/* Google Calendar */}
        <section className="surface-card overflow-hidden" data-testid="gcal-section">
          <div className="px-5 py-3.5 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-cyan-600" />
              <h3 className="text-sm font-semibold text-foreground">Google Calendar</h3>
            </div>
          </div>
          <div className="px-5 py-4 space-y-3">
            {gcalStatus.connected ? (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">Connected</p>
                    <p className="text-[11px] text-muted-foreground">{gcalStatus.google_email}</p>
                  </div>
                  <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="btn-primary flex-1" onClick={handleSyncGcal} disabled={syncLoading} data-testid="gcal-sync-btn">
                    {syncLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <CalendarDays className="w-3.5 h-3.5 mr-1.5" />}
                    Sync Now
                  </Button>
                  <Button size="sm" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={handleDisconnectGcal} disabled={gcalLoading} data-testid="gcal-disconnect-btn">
                    {gcalLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Disconnect'}
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground">Events sync both ways: CircleUpSquad events appear in Google Calendar and vice versa.</p>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">Connect your Google Calendar to sync events in both directions.</p>
                <Button size="sm" className="w-full btn-primary" onClick={handleConnectGcal} disabled={gcalLoading} data-testid="gcal-connect-btn">
                  {gcalLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <CalendarDays className="w-3.5 h-3.5 mr-1.5" />}
                  Connect Google Calendar
                </Button>
              </>
            )}
          </div>
        </section>

        {/* Availability */}
        {user.role !== 'patient' && (
          <section className="surface-card overflow-hidden" data-testid="availability-section">
            <div className="px-5 py-3.5 border-b border-border bg-muted/30">
              <div className="flex items-center gap-2">
                {availability.paused ? <PauseCircle className="w-4 h-4 text-amber-600" /> : <PlayCircle className="w-4 h-4 text-emerald-600" />}
                <h3 className="text-sm font-semibold text-foreground">Availability</h3>
              </div>
            </div>
            <div className="px-5 py-4 space-y-3">
              {availability.paused ? (
                <>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Paused until {new Date(availability.paused_until).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                    {availability.reason && <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">{availability.reason}</p>}
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Your patient and squad can see you're away. Notifications are paused.</p>
                  </div>
                  <Button size="sm" className="w-full btn-primary" onClick={handleResume} disabled={pauseLoading} data-testid="resume-btn">
                    {pauseLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <PlayCircle className="w-3.5 h-3.5 mr-1.5" />}
                    I'm back — resume notifications
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">Need to step away? Pause notifications and let your patient know you're unavailable.</p>
                  <div className="space-y-2">
                    <Label htmlFor="pause-date" className="text-xs">Unavailable until</Label>
                    <Input id="pause-date" type="date" value={pauseDate} onChange={(e) => setPauseDate(e.target.value)} min={new Date().toISOString().split('T')[0]} data-testid="pause-date-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pause-reason" className="text-xs">Reason (optional — visible to patient)</Label>
                    <Input id="pause-reason" placeholder="e.g., traveling, personal time" value={pauseReason} onChange={(e) => setPauseReason(e.target.value)} data-testid="pause-reason-input" />
                  </div>
                  <Button size="sm" className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={handlePause} disabled={pauseLoading || !pauseDate} data-testid="pause-btn">
                    {pauseLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <PauseCircle className="w-3.5 h-3.5 mr-1.5" />}
                    Pause notifications
                  </Button>
                </>
              )}
            </div>
          </section>
        )}

        {/* Privacy */}
        <section className="surface-card overflow-hidden" data-testid="privacy-section">
          <div className="px-5 py-3.5 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-cyan-600" />
              <h3 className="text-sm font-semibold text-foreground">Privacy</h3>
            </div>
          </div>
          <div className="px-5 py-3 text-xs text-muted-foreground space-y-1.5">
            <p>Your data is stored securely and only shared within your circles.</p>
            <p>Notification emails and phone numbers are only used for delivery.</p>
          </div>
        </section>

        {/* Sign Out */}
        <button
          onClick={handleLogout}
          className="w-full surface-card px-5 py-4 flex items-center gap-3 text-destructive hover:bg-destructive/10 transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <p className="text-sm font-semibold">Sign Out</p>
        </button>

        {/* Admin Console - only for admin users */}
        {user?.is_admin && (
          <button
            onClick={() => navigate('/admin')}
            className="w-full surface-card px-5 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors"
            data-testid="admin-link"
          >
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-amber-600" />
              <div className="text-left">
                <p className="text-sm font-medium text-foreground">Admin Console</p>
                <p className="text-[11px] text-muted-foreground">Manage users, circles, and promo codes</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
      </main>
    </div>
  );
};

export default SettingsPage;
